import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import { ArrowLeft, Clock, User, Calendar, ArrowRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getBlogPost(slug)
      .then(data => {
        setPost(data);
        setLoading(false);
        // Get related posts
        api.getBlogPosts().then(all => {
          setRelatedPosts(all.filter(p => p.slug !== slug).slice(0, 3));
        });
      })
      .catch(() => { setPost(null); setLoading(false); });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-brand-200 border-t-brand-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Article Not Found</h1>
        <p className="text-gray-500 mb-6">The article you're looking for doesn't exist.</p>
        <Link to="/blog">
          <Button className="bg-brand-500 hover:bg-brand-700 text-white rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category}, immigration, visa, ${post.title.toLowerCase()}`}
        image={post.image}
        type="article"
      />
      {/* Hero */}
      <section className="relative py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-sm text-brand-500 hover:text-brand-700 font-medium mb-8 group">
            <ArrowLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </Link>
          <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
            {post.category}
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-gray-900 leading-tight mb-6">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4 text-brand-400" /> {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-brand-400" /> {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-brand-400" /> {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="rounded-3xl overflow-hidden shadow-lg">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-[300px] sm:h-[400px] object-cover"
          />
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="prose prose-lg max-w-none">
          {post.content.split('\n\n').map((paragraph, index) => (
            <p key={index} className="text-gray-600 leading-relaxed mb-6 text-base">
              {paragraph}
            </p>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 bg-brand-50 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-3">Need Expert Guidance?</h3>
          <p className="text-gray-500 mb-5 text-sm">Our immigration experts are ready to help you with personalized advice.</p>
          <Link to="/contact">
            <Button className="bg-brand-500 hover:bg-brand-700 text-white rounded-xl h-11 px-6 text-sm font-semibold shadow-lg shadow-brand-200/60 transition-all duration-300 hover:-translate-y-0.5">
              Book Free Consultation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((rPost) => (
                <Link key={rPost.id} to={`/blog/${rPost.slug}`}>
                  <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group overflow-hidden cursor-pointer h-full">
                    <div className="relative overflow-hidden">
                      <img
                        src={rPost.image}
                        alt={rPost.title}
                        className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-5">
                      <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 text-xs font-medium rounded-lg mb-2">
                        {rPost.category}
                      </Badge>
                      <h3 className="font-semibold text-gray-900 text-sm group-hover:text-brand-700 transition-colors leading-snug">
                        {rPost.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
